'use client';

import { Check, Info, Phone, Plus, X } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { MultiFormatImage } from '@/components/ui/MultiFormatImage';
import { useInquiry } from '@/contexts/InquiryContext';

export default function AccessoryCard({ accessory }) {
    const router = useRouter();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const modalRef = useRef(null);
    const scrollPositionRef = useRef(0);

    const { addToInquiry, isInInquiry } = useInquiry();

    const handleAddToInquiry = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        setIsAdding(true);

        const inquiryItem = {
            type: 'accessory',
            name: accessory.name,
            image: accessory.img,
            description: accessory.description,
            specifications: accessory.specs
                ? accessory.specs.reduce((acc, spec) => {
                      acc[spec.label] = spec.value;
                      return acc;
                  }, {})
                : {},
        };

        addToInquiry(inquiryItem);

        // Reset the adding state after a short delay
        setTimeout(() => {
            setIsAdding(false);
        }, 1000);
    };

    const isInList = isInInquiry({
        type: 'accessory',
        name: accessory.name,
    });

    // Handle clicking outside the modal to close it
    useEffect(() => {
        function handleClickOutside(event) {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setIsModalOpen(false);
            }
        }

        if (isModalOpen) {
            // Save current scroll position
            scrollPositionRef.current = window.scrollY;

            // Set CSS variable for scroll position
            document.documentElement.style.setProperty(
                '--scroll-position',
                `-${scrollPositionRef.current}px`,
            );

            // Add modal-open class to body
            document.body.classList.add('modal-open');

            document.addEventListener('mousedown', handleClickOutside);
        } else {
            // Remove modal-open class
            document.body.classList.remove('modal-open');

            // Restore scroll position
            if (
                document.documentElement.style.getPropertyValue(
                    '--scroll-position',
                )
            ) {
                document.documentElement.style.removeProperty(
                    '--scroll-position',
                );
                window.scrollTo(0, scrollPositionRef.current);
            }

            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isModalOpen]);

    // Handle escape key to close modal
    useEffect(() => {
        function handleEscKey(event) {
            if (event.key === 'Escape') {
                setIsModalOpen(false);
            }
        }

        if (isModalOpen) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isModalOpen]);

    // Clean up when component unmounts
    useEffect(() => {
        return () => {
            // Ensure we clean up body styles when component unmounts
            if (document.body.classList.contains('modal-open')) {
                document.body.classList.remove('modal-open');
                document.documentElement.style.removeProperty(
                    '--scroll-position',
                );
            }
        };
    }, []);

    return (
        <>
            <div className="group bg-gradient-to-br from-white to-gray-50 hover:to-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border border-gray-200 hover:border-brand/40 h-full flex flex-col">
                <div className="relative bg-linear-to-br from-gray-50 to-gray-100 aspect-[4/3] flex items-center justify-center overflow-hidden">
                    <div className="w-full h-full p-4 flex items-center justify-center">
                        <MultiFormatImage
                            src={accessory.img || '/logo/trenchmate-logo.svg'}
                            alt={accessory.name}
                            width={300}
                            height={200}
                            className="w-full h-full object-contain"
                            sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 25vw"
                        />
                    </div>
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-linear-to-t from-brand/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>

                <div className="p-5 flex flex-col grow transform-none">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-brand transition-colors">
                        {accessory.name}
                    </h3>

                    <div className="mt-auto space-y-2">
                        {/* Contact Us or Add to Inquiry button */}
                        {accessory.inquiry === true ? (
                            <button
                                type="button"
                                onClick={handleAddToInquiry}
                                disabled={isAdding}
                                className={`w-full py-2 px-3 font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2 active:scale-[0.97] hover:cursor-pointer uppercase tracking-wide rounded-none ${
                                    isInList
                                        ? 'bg-green-100 text-green-700 border border-green-200'
                                        : isAdding
                                          ? 'bg-brand/20 text-brand'
                                          : 'bg-brand-dark text-white border border-brand-dark hover:bg-white hover:text-brand-dark'
                                }`}
                            >
                                {isAdding ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Adding...
                                    </>
                                ) : isInList ? (
                                    <>
                                        <Check className="w-4 h-4" />
                                        Added
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-4 h-4" />
                                        Add to Inquiry
                                    </>
                                )}
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => {
                                    router.push('/contact');
                                }}
                                className="w-full py-2 px-3 bg-brand-dark text-white border border-brand-dark hover:bg-white hover:text-brand-dark transition-colors duration-200 font-medium text-sm flex items-center justify-center gap-2 active:scale-[0.97] hover:cursor-pointer cursor-pointer uppercase tracking-wide rounded-none"
                            >
                                <Phone className="w-4 h-4" />
                                Contact Us
                            </button>
                        )}

                        {/* View details button — link to detail page for
                            high-value accessories with a slug, otherwise open modal */}
                        {accessory.slug ? (
                            <Link
                                href={`/products/accessories/${accessory.slug}`}
                                className="w-full py-2 px-3 bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors flex items-center justify-center text-sm font-medium uppercase tracking-wide rounded-none"
                            >
                                <Info className="w-4 h-4 mr-2" />
                                View Details
                            </Link>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(true)}
                                className="w-full py-2 px-3 bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors flex items-center justify-center text-sm font-medium hover:cursor-pointer uppercase tracking-wide rounded-none"
                            >
                                <Info className="w-4 h-4 mr-2" />
                                View Specifications
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div
                        ref={modalRef}
                        className="bg-white shadow-2xl max-w-2xl w-[calc(100vw-2rem)] max-h-[90vh] flex flex-col overflow-hidden animate-fadeIn my-8"
                    >
                        {/* Modal header */}
                        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                            <h3 className="text-xl font-bold text-gray-900">
                                {accessory.name}
                            </h3>
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                                aria-label="Close modal"
                            >
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        {/* Modal content */}
                        <div className="p-6 overflow-y-auto grow">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {/* Image section */}
                                <div className="bg-gray-50 rounded-lg p-6 flex items-center justify-center">
                                    <MultiFormatImage
                                        src={
                                            accessory.img ||
                                            '/logo/trenchmate-logo.svg'
                                        }
                                        alt={accessory.name}
                                        width={400}
                                        height={400}
                                        className="max-h-[300px] w-auto object-contain"
                                    />
                                </div>

                                {/* Specs section */}
                                <div>
                                    <h4 className="text-lg font-bold text-gray-800 mb-4">
                                        Specifications
                                    </h4>

                                    {accessory.specs &&
                                    accessory.specs.length > 0 ? (
                                        <div className="space-y-3">
                                            {accessory.specs.map((spec) => (
                                                <div
                                                    key={spec.label}
                                                    className="border-b border-gray-100 pb-2 last:border-0"
                                                >
                                                    <p className="text-sm font-medium text-gray-600">
                                                        {spec.label}
                                                    </p>
                                                    <p className="text-md font-semibold text-gray-900">
                                                        {spec.value}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-gray-500 italic">
                                            No specifications available
                                        </p>
                                    )}

                                    {/* Description if available */}
                                    {accessory.description && (
                                        <div className="mt-6">
                                            <h4 className="text-lg font-bold text-gray-800 mb-2">
                                                Description
                                            </h4>
                                            <p className="text-gray-700">
                                                {accessory.description}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Modal footer */}
                        <div className="px-6 py-4 border-t border-gray-100 flex justify-end items-center gap-3">
                            {accessory.inquiry === true ? (
                                <button
                                    type="button"
                                    onClick={handleAddToInquiry}
                                    disabled={isAdding}
                                    className={`py-3 px-6 font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2 active:scale-[0.97] uppercase tracking-wide rounded-lg ${
                                        isInList
                                            ? 'bg-green-100 text-green-700 border border-green-200'
                                            : isAdding
                                              ? 'bg-brand/20 text-brand'
                                              : 'bg-brand-dark text-white border border-brand-dark hover:bg-white hover:text-brand-dark'
                                    }`}
                                >
                                    {isAdding ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-brand border-t-transparent rounded-full animate-spin"></div>
                                            Adding...
                                        </>
                                    ) : isInList ? (
                                        <>
                                            <Check className="w-4 h-4" />
                                            Added to Inquiry
                                        </>
                                    ) : (
                                        <>
                                            <Plus className="w-4 h-4" />
                                            Add to Inquiry
                                        </>
                                    )}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => {
                                        router.push('/contact');
                                    }}
                                    className="py-3 px-6 font-medium text-sm transition-colors duration-200 flex items-center justify-center gap-2 bg-brand-dark text-white border border-brand-dark hover:bg-white hover:text-brand-dark active:scale-[0.97] uppercase tracking-wide rounded-lg"
                                >
                                    <Phone className="w-4 h-4" />
                                    Contact Us
                                </button>
                            )}

                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors duration-200 font-medium text-sm focus:outline-none focus:ring-1 focus:ring-slate-400 uppercase tracking-wide rounded-lg"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
